import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { EventsPaginatedService } from '../../services/events-paginated.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserRoles } from 'api/models';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { UsersService } from 'api/services';
import { lastValueFrom } from 'rxjs';
import { getEvents } from '@app/stores/eventsStore';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-overview-buttons',
    templateUrl: './overview-buttons.component.html',
    styleUrls: ['./overview-buttons.component.scss'],
    standalone: false,
})
export class OverviewButtonsComponent implements OnInit {
    @Output()
    selectedView: EventEmitter<string> = new EventEmitter<string>();
    @Input() viewType = 'folder';
    newNotifications: boolean = false;

    constructor(
        private eventsPaginatedService: EventsPaginatedService,
        private sessionService: SessionService,
        private usersService: UsersService
    ) {}
    hasSelfServiceAccess = false;

    allowedRoles = [
        UserRoles.Carrier,
        UserRoles.Consignee,
        UserRoles.Consignor,
        UserRoles.OrganizationAdmin,
        UserRoles.SiteAdmin,
        UserRoles.SelfCheckin,
    ];

    async ngOnInit(): Promise<void> {
        await this.hasSelfService();
        if (this.hasSelfServiceAccess) {
            // Only subscribe to notifications, don't start polling here
            // The polling is controlled by the consignee overview component
            this.eventsPaginatedService.newNotifications$
                .pipe(untilDestroyed(this))
                .subscribe(newNotifications => {
                    this.newNotifications = newNotifications;
                });
        }
    }

    /**
     * Changes the view type based on the button toggle change event.
     * If the 'notifications' view is selected, it sets new notifications to false.
     * @param event The button toggle change event.
     */
    changeView(event: MatButtonToggleChange): void {
        this.selectedView.emit(event.value);
        this.viewType = event.value;
        if (event.value === 'notifications') {
            this.eventsPaginatedService.setNewNotificationsToFalse();
        }
    }

    /**
     * Checks if the user has self-service check-in access.
     * Retrieves the organization sites and filters them based on user roles.
     * Sets the `hasSelfServiceAccess` property based on the presence of self-checkin enabled sites.
     */
    async hasSelfService() {
        const hasSelfCheckin = this.sessionService
            .getMyOrganizationSites()
            .filter(allSite =>
                allSite.roles!.some((role: UserRoles) =>
                    this.allowedRoles.includes(role)
                )
            )
            .some(allSite => allSite.selfCheckin);
        this.hasSelfServiceAccess = hasSelfCheckin;
    }
}
